/**
 * JourneyDetailsModal Component
 *
 * Modal displaying detailed journey information.
 */

import { useTranslation, useLocale } from '@tonnie/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@tonnie/ui';
import { JourneyInstance } from '@/core/contexts/AuthContext';
import { journeyScenarioLabel } from '../utils/journey-scenario-label';
import { JourneySummary } from './JourneySummary';

interface IJourneyDetailsModalProps {
  instance: JourneyInstance | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function JourneyDetailsModal({ instance, open, onOpenChange }: IJourneyDetailsModalProps) {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  if (!instance) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        closeLabel={t('common.close')}
      >
        <DialogHeader>
          <DialogTitle>{t('journey.details.title')}</DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-6 pt-2">
          <JourneySummary className="border-0 shadow-none" />

          {/* Additional journey details */}
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('journey.details.scenario')}</p>
                <p className="font-medium">{journeyScenarioLabel(instance.scenario, t)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('journey.details.status')}</p>
                <p className="font-medium capitalize">
                  {t(`journey.lifecycle.${instance.lifecycleState}`)}
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-2">
                {t('journey.details.featuresIncluded')}
              </p>
              <div className="flex flex-wrap gap-2">
                {instance.scope.checkup && (
                  <span className="px-2 py-1 rounded-full bg-primary/10 text-primary-accessible text-xs">
                    {t('heroes.checkup.title')}
                  </span>
                )}
                {instance.scope.teleportal && (
                  <span className="px-2 py-1 rounded-full bg-info/10 text-info-accessible text-xs">
                    {t('heroes.teleportal.title')}
                  </span>
                )}
                {instance.scope.qultureup && (
                  <span className="px-2 py-1 rounded-full bg-primary/10 text-primary-accessible text-xs">
                    {t('heroes.qultureup.title')}
                  </span>
                )}
                {instance.scope.care && (
                  <span className="px-2 py-1 rounded-full bg-warning/10 text-warning-accessible text-xs">
                    {t('heroes.care.title')}
                  </span>
                )}
                {instance.scope.passport && (
                  <span className="px-2 py-1 rounded-full bg-success/10 text-success-accessible text-xs">
                    {t('heroes.passport.title')}
                  </span>
                )}
              </div>
            </div>

            {instance.paymentInfo && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">
                  {t('journey.details.paymentInfo')}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('journey.details.type')}</p>
                    <p className="font-medium capitalize">{instance.paymentInfo.paymentType}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('journey.details.amount')}</p>
                    <p className="font-medium capitalize">
                      {formatCurrency(instance.paymentInfo.amount)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
